import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { resolvePlaygroundItemId } from "../../utils/playgroundDelete";

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
const BACKGROUND_NAME_ALLOWED_REGEX = /^[a-zA-Z0-9\s\-_\(\)]*$/;
const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;

const normalizeGender = (val, fallback = "") => {
  if (!val) return fallback;
  const s = String(val).trim().toLowerCase();
  if (s === "men" || s === "male" || s === "m") return "men";
  if (s === "women" || s === "female" || s === "w") return "women";
  if (s === "unisex" || s === "neutral" || s === "all" || s === "both") return "unisex";
  return fallback;
};

export const validateBackgroundName = (name) => {
  const raw = String(name || "");
  const trimmedName = raw.trim().replace(/\s+/g, " ");

  if (!raw || !trimmedName) {
    return "Name is required";
  }
  if (trimmedName.length < 3) {
    return "Name must be at least 3 characters";
  }
  if (trimmedName.length > 100) {
    return "Name can be at most 100 characters";
  }
  if (!BACKGROUND_NAME_ALLOWED_REGEX.test(raw)) {
    return "Name can only contain letters, numbers, spaces, -, _, and ()";
  }
  if (!CONTAINS_LETTER_REGEX.test(trimmedName)) {
    return "Name must contain at least one letter";
  }
  return "";
};

export const validateBackgroundGender = (gender) => {
  if (!gender || !String(gender).trim()) {
    return "Please select a gender";
  }
  const lower = String(gender).toLowerCase();
  if (!["men", "women", "unisex"].includes(lower)) {
    return "Invalid gender selected";
  }
  return "";
};

export const useBackgroundUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  backgroundToEdit = null,
  defaultGender = "",
}) => {
  const isEditMode = Boolean(backgroundToEdit);
  const [formData, setFormData] = useState({
    sName: "",
    gender: "",
    image: null,
  });

  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const formRef = useRef(null);

  const normalizeBackgroundName = (name) =>
    name.trim().toLowerCase().replace(/\s+/g, "_");

  const escapeRegexSpecialChars = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const validateFileName = (file, backgroundName) => {
    if (!backgroundName || !backgroundName.trim()) {
      return { valid: false, error: "Please enter a background name first" };
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
      return { valid: false, error: `Image must be in jpg, jpeg, or png format. Got: ${fileExtension}` };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds 50MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
      };
    }

    const escapedBackgroundName = escapeRegexSpecialChars(normalizeBackgroundName(backgroundName));
    const expectedNamePattern = new RegExp(`^${escapedBackgroundName}\\.(jpg|jpeg|png)$`, "i");

    if (!expectedNamePattern.test(file.name)) {
      const expectedFormat = `${backgroundName.trim().toLowerCase().replace(/\s+/g, "_")}.jpg (or .jpeg or .png)`;
      return {
        valid: false,
        error: `File name format incorrect. Expected: ${expectedFormat}. Got: ${file.name}`,
      };
    }

    return { valid: true };
  };

  const prevBackgroundRef = useRef(backgroundToEdit);
  const isFirstMountRef = useRef(true);

  const resetForm = useCallback(() => {
    if (backgroundToEdit) {
      const initialGender = normalizeGender(
        backgroundToEdit.gender ||
        backgroundToEdit.sGender ||
        backgroundToEdit.category ||
        backgroundToEdit.eGender ||
        backgroundToEdit.genderName,
        defaultGender || ""
      );
      setFormData({
        sName: backgroundToEdit.sName || backgroundToEdit.name || backgroundToEdit.title || "",
        gender: initialGender,
        image: null,
      });
      setPreviewUrl(backgroundToEdit.image || backgroundToEdit.front || backgroundToEdit.images?.[0] || null);
    } else {
      setFormData({ sName: "", gender: "", image: null });
      setPreviewUrl(null);
    }
    setErrors({});
    setZoomImage(null);
  }, [backgroundToEdit, defaultGender]);

  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      if (backgroundToEdit) {
        resetForm();
      }
      return;
    }

    const prevBg = prevBackgroundRef.current;
    const prevId = prevBg?.iBackgroundId ?? prevBg?.id ?? prevBg?.backgroundId ?? null;
    const currId = backgroundToEdit?.iBackgroundId ?? backgroundToEdit?.id ?? backgroundToEdit?.backgroundId ?? null;

    const isBgChanged = prevId !== currId || Boolean(prevBg) !== Boolean(backgroundToEdit);

    if (isBgChanged) {
      prevBackgroundRef.current = backgroundToEdit;
      resetForm();
    }
  }, [backgroundToEdit, resetForm]);

  // Change detection for edit mode
  const hasChanges = useMemo(() => {
    if (!isEditMode) return true;
    const currentName = formData.sName.trim();
    const originalName = (backgroundToEdit?.sName || backgroundToEdit?.name || backgroundToEdit?.title || "").trim();
    const currentGender = formData.gender;
    const originalGender = normalizeGender(
      backgroundToEdit?.gender ||
      backgroundToEdit?.sGender ||
      backgroundToEdit?.category ||
      backgroundToEdit?.eGender ||
      backgroundToEdit?.genderName,
      defaultGender || "women"
    );
    const nameChanged = currentName !== originalName;
    const genderChanged = currentGender !== originalGender;
    const imageChanged = Boolean(formData.image);
    return nameChanged || genderChanged || imageChanged;
  }, [isEditMode, formData, backgroundToEdit, defaultGender]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue =
      name === "sName"
        ? value.replace(/^\s+/, "").replace(/\s{2,}/g, " ")
        : value;

    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: sanitizedValue };

      if (name === "sName") {
        const nameError = validateBackgroundName(sanitizedValue);
        if (prev.image) {
          const imageValidation = validateFileName(prev.image, sanitizedValue);
          setErrors((prevErrors) => ({
            ...prevErrors,
            sName: nameError,
            image: imageValidation.valid ? "" : imageValidation.error,
          }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            sName: nameError,
          }));
        }
      } else if (name === "gender") {
        const genderError = validateBackgroundGender(sanitizedValue);
        setErrors((prevErrors) => ({
          ...prevErrors,
          gender: genderError,
        }));
      } else if (errors[name]) {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }

      return updatedFormData;
    });
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    if (name === "sName") {
      const nameError = validateBackgroundName(value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        sName: nameError,
      }));
    } else if (name === "gender") {
      const genderError = validateBackgroundGender(value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        gender: genderError,
      }));
    }
  };

  const isFormValid = useMemo(() => {
    const nameError = validateBackgroundName(formData.sName);
    if (nameError) return false;

    const genderError = validateBackgroundGender(formData.gender);
    if (genderError) return false;

    if (!formData.image) {
      if (!isEditMode && !previewUrl) {
        return false;
      }
    } else {
      const imageValidation = validateFileName(formData.image, formData.sName);
      if (!imageValidation.valid) return false;
    }

    if (errors.sName || errors.gender || errors.image) {
      return false;
    }

    return true;
  }, [formData, previewUrl, errors, isEditMode]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFileName(file, formData.sName);

    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, image: validation.error }));
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    setFormData((prev) => ({ ...prev, image: file }));

    if (errors.image) {
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setPreviewUrl(null);
    const inputElement = document.getElementById("backgroundImageModal");
    if (inputElement) {
      inputElement.value = "";
    }
    if (errors.image) {
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateBackgroundName(formData.sName);
    if (nameError) {
      newErrors.sName = nameError;
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!formData.image) {
      if (!isEditMode && !previewUrl) {
        newErrors.image = "Image is required";
      }
    } else {
      const imageValidation = validateFileName(formData.image, formData.sName);
      if (!imageValidation.valid) newErrors.image = imageValidation.error;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstErrorKey = Object.keys(newErrors)[0];
        const errorElement = formRef.current?.querySelector(`[name="${firstErrorKey}"], #${firstErrorKey}`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus({ preventScroll: true });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const cleanName = formData.sName.trim().replace(/\s+/g, " ");

    if (isEditMode) {
      const backgroundId =
        resolvePlaygroundItemId(backgroundToEdit) ||
        backgroundToEdit?.iBackgroundId ||
        backgroundToEdit?.backgroundId ||
        backgroundToEdit?.id;

      const formDataToSend = new FormData();
      formDataToSend.append("sName", cleanName);
      formDataToSend.append("gender", formData.gender);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      onSubmit(formDataToSend, true, backgroundId);
    } else {
      const formDataToSend = new FormData();
      formDataToSend.append("sName", cleanName);
      formDataToSend.append("gender", formData.gender);
      formDataToSend.append("image", formData.image);
      onSubmit(formDataToSend, false, null);
    }
  };

  const isFormDirty = useMemo(() => {
    if (isEditMode) {
      return hasChanges;
    }
    return Boolean(
      (formData.sName && formData.sName.trim()) ||
        Boolean(formData.gender) ||
        formData.image ||
        Object.keys(errors).length > 0
    );
  }, [isEditMode, hasChanges, formData, errors]);

  const handleReset = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleCancel = useCallback(() => {
    resetForm();
    if (onClose) {
      onClose();
    }
  }, [resetForm, onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e && e.target !== e.currentTarget) return;
      if (onClose) {
        onClose();
      }
    },
    [onClose]
  );

  const handleClose = handleCancel;

  return {
    isEditMode,
    hasChanges,
    isFormValid,
    isFormDirty,
    formData,
    setFormData,
    errors,
    previewUrl,
    zoomImage,
    setZoomImage,
    formRef,
    handleInputChange,
    handleInputBlur,
    handleImageChange,
    handleRemoveImage,
    handleSubmit,
    handleClose,
    handleReset,
    handleCancel,
    handleBackdropClick,
  };
};
